// frontend/src/pages/ConversionCard.jsx
// Phase 3 step 11: on a chart page, what updating a chart from the previous
// builder would do -- the plan the server worked out: how the new chart
// reads, what has no equivalent and is left out, or why it cannot be done
// yet -- with the Update button for admins and analysts. Nothing is changed
// until that button is pressed, and a converted chart can be put back.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Loader2, RotateCcw, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { Card, CardContent } from '@/components/ui/card.jsx';
import { convertChart, getConversionPlan, revertConversion } from '../charts/semanticApi';
import { isAnalystOrHigher } from '../utils/roles';

const TYPE_WORDS = { line: 'line', bar: 'bar', area: 'area', stackedBar: 'stacked bar', horizontalBar: 'horizontal bar',
  doughnut: 'donut', card: 'KPI', table: 'table', matrix: 'matrix' };

function List({ title, items, tone }) {
  if (!items || !items.length) return null;
  const colour = tone === 'warn' ? 'text-amber-400' : tone === 'bad' ? 'text-red-400' : 'text-[#e5e5e5]';
  return (
    <div>
      <p className="mb-1 text-xs text-[#6a6a6a]">{title}</p>
      <ul className={`space-y-0.5 text-sm ${colour}`}>
        {items.map((t) => <li key={t}>{t}</li>)}
      </ul>
    </div>
  );
}

/** For a chart from the previous builder (no spec): the plan and the Update button. */
export function UpdateChartCard({ chart, onConverted }) {
  const navigate = useNavigate();
  const [plan, setPlan] = useState({ status: 'loading', plan: null, error: null });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const ctrl = new AbortController();
    getConversionPlan(chart.id, { signal: ctrl.signal })
      .then((p) => { if (!ctrl.signal.aborted) setPlan({ status: 'ready', plan: p, error: null }); })
      .catch((e) => { if (!ctrl.signal.aborted) setPlan({ status: 'error', plan: null, error: e }); });
    return () => ctrl.abort();
  }, [chart.id]);

  async function update() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const { chart: converted } = await convertChart(chart.id);
      if (onConverted) onConverted(converted);
      navigate(`/charts/build?chart=${converted.id}`);
    } catch (e) {
      setError(e && e.code === 'FORBIDDEN' ? 'Only admins and analysts can update charts.'
        : (e && e.message) || "The chart couldn't be updated. Try again.");
      setBusy(false);
    }
  }

  const p = plan.plan;
  return (
    <Card className="border-blue-500/30">
      <CardContent className="space-y-4 p-6">
        <div className="flex items-start gap-3">
          <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-blue-400" aria-hidden="true" />
          <div>
            <h3 className="text-base font-semibold text-white">This chart was made with the previous builder</h3>
            <p className="mt-1 text-sm text-[#a0a0a0]">
              Updating it gives it a real query over your synced data, so it draws and can be edited in the new builder.
              Nothing changes until you press Update, and you can put it back afterwards.
            </p>
          </div>
        </div>

        {plan.status === 'loading' && <Loader2 className="h-4 w-4 animate-spin text-[#6a6a6a]" aria-label="Working out the update" />}
        {plan.status === 'error' && <p role="alert" className="text-sm text-red-400">The update plan couldn't be loaded.</p>}
        {p && (
          <div className="space-y-3">
            {p.status === 'ready' && (
              <p className="text-sm text-[#e5e5e5]">
                It becomes a <span className="font-medium">{TYPE_WORDS[p.chart_type] || p.chart_type}</span> chart on{' '}
                <span className="font-medium">{p.dataset_name || 'its dataset'}</span>.
              </p>
            )}
            <List title="The new chart" items={p.notes} />
            <List title="Left out (no equivalent in the new builder)" items={p.dropped} tone="warn" />
            <List title="Can't update yet" items={p.blockers} tone="bad" />
          </div>
        )}

        {error && <p role="alert" className="text-sm text-red-400">{error}</p>}
        {p && p.status === 'ready' && isAnalystOrHigher() && (
          <Button onClick={update} disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <ArrowRight className="h-4 w-4" aria-hidden="true" />}
            {busy ? 'Updating…' : 'Update and open in the builder'}
          </Button>
        )}
        {p && p.status === 'ready' && !isAnalystOrHigher() && (
          <p className="text-xs text-[#6a6a6a]">An admin or analyst can update this chart.</p>
        )}
      </CardContent>
    </Card>
  );
}

/** For a chart that was converted: when, and Undo. */
export function ConvertedNote({ chart, onReverted }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  if (!chart.converted_from_legacy_at) return null;
  const when = new Date(chart.converted_from_legacy_at);
  async function undo() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const back = await revertConversion(chart.id);
      if (onReverted) onReverted(back);
    } catch (e) {
      setError((e && e.message) || "The chart couldn't be put back. Try again.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#6a6a6a]">
      <span>Updated from the previous builder{Number.isNaN(when.getTime()) ? '' : ` on ${when.toLocaleDateString()}`}.</span>
      {isAnalystOrHigher() && (
        <button type="button" onClick={undo} disabled={busy} className="inline-flex items-center gap-1 underline hover:text-white disabled:opacity-50">
          <RotateCcw className="h-3 w-3" aria-hidden="true" />{busy ? 'Putting back…' : 'Put it back as it was'}
        </button>
      )}
      {error && <span role="alert" className="text-red-400">{error}</span>}
    </div>
  );
}